import { useMemo } from "react";

import { isReferencedNodeValue } from "@doitintl/cloudflow-commons";
import { type CloudFlowNodeType, CloudFlowProvider, type StringApiServiceModelDescriptor } from "@doitintl/cmp-models";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, FormControl, IconButton, InputAdornment, MenuItem, TextField, Tooltip } from "@mui/material";
import { type FieldInputProps } from "formik";

import VirtualizedAutocomplete from "../../../../../Components/Autocomplete/VirtualizedAutocomplete";
import { useCustomerContext } from "../../../../../Context/CustomerContext";
import { useToggle } from "../../../../../utils/useToggle";
import { useGetGcpProjects } from "../../../hooks";
import { useNodeConfigurationContext } from "../../ConfigurationPanel/NodeConfigurationContext";
import { useFieldCommonProps } from "../useFieldCommonProps";
import { OptionalParamWrapper } from "./wrappers/OptionalParamWrapper";
import { ReferencedFieldWrapper } from "./wrappers/ReferencedField/ReferencedFieldWrapper";

type GCPProjectStringParamProps = {
  fieldProps: FieldInputProps<string>;
  inputModel: StringApiServiceModelDescriptor;
  label: string;
  onRemove?: () => void;
  disallowReferencedField?: boolean;
  renderAsNotRequired?: boolean;
};
export const GCPProjectStringParam = ({
  fieldProps,
  inputModel,
  label,
  onRemove,
  disallowReferencedField,
  renderAsNotRequired,
}: GCPProjectStringParamProps) => {
  const commonFieldProps = useFieldCommonProps(fieldProps, label, onRemove === undefined && !renderAsNotRequired);
  const { nodeConfig } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const { customer } = useCustomerContext();
  const [isVisible, , , toggleVisibility] = useToggle(false);

  const serviceAccountResource =
    nodeConfig?.parameters?.provider === CloudFlowProvider.GCP
      ? nodeConfig.parameters.configurationValues.serviceAccountResource || ""
      : "";

  const { data: projects, isLoading: gcpProjectsLoading } = useGetGcpProjects(
    customer.id,
    serviceAccountResource,
    serviceAccountResource.includes("organizations/")
  );

  const projectIds = useMemo(() => projects?.map((proj) => proj.projectId).filter(Boolean) || [], [projects]);

  const endAdornment = inputModel.sensitive ? (
    <InputAdornment position="end">
      <IconButton aria-label="toggle value visibility" onClick={toggleVisibility} edge="end">
        {isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    </InputAdornment>
  ) : undefined;

  const gcpProjectField = (
    <FormControl fullWidth error={commonFieldProps.error}>
      <VirtualizedAutocomplete
        loading={gcpProjectsLoading}
        options={projectIds}
        value={commonFieldProps.value || null}
        freeSolo
        forcePopupIcon
        onChange={(_, newValue) => {
          commonFieldProps.onChange({
            target: {
              value: newValue || "",
              name: commonFieldProps.name,
            },
          });
        }}
        onBlur={() => {
          commonFieldProps.onBlur({
            target: {
              name: commonFieldProps.name,
            },
          });
        }}
        onInputChange={(_, newInputValue) => {
          commonFieldProps.onChange({
            target: {
              value: newInputValue,
              name: commonFieldProps.name,
            },
          });
        }}
        renderOption={(props, option) => (
          <MenuItem {...props} value={option}>
            {option}
          </MenuItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={commonFieldProps.label}
            error={commonFieldProps.error}
            helperText={commonFieldProps.helperText}
            required={commonFieldProps.required}
            size="small"
            variant="outlined"
            fullWidth
            placeholder={!serviceAccountResource ? "No organization selected" : "Select or type a project ID"}
          />
        )}
        size="small"
        fullWidth
        disabled={!serviceAccountResource}
      />
    </FormControl>
  );

  const textField = (
    <TextField
      {...commonFieldProps}
      type={!inputModel.sensitive || isVisible ? "text" : "password"}
      fullWidth
      select={inputModel.enum !== undefined}
      multiline={inputModel.multiline}
      rows={4}
      maxRows={10}
      slotProps={{
        input: {
          endAdornment,
        },
      }}
    >
      {inputModel.enum?.map((itemValue, index) => (
        <MenuItem key={itemValue} value={itemValue}>
          <Tooltip
            title={inputModel.enumDescriptions?.[index]}
            disableFocusListener
            enterDelay={1000}
            placement="right"
            arrow
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 8],
                    },
                  },
                ],
              },
            }}
          >
            <Box flex={1}>{itemValue}</Box>
          </Tooltip>
        </MenuItem>
      ))}
    </TextField>
  );

  const fieldToRender = !isReferencedNodeValue(commonFieldProps.value) ? gcpProjectField : textField;

  return (
    <OptionalParamWrapper onRemove={onRemove}>
      {disallowReferencedField ? (
        fieldToRender
      ) : (
        <ReferencedFieldWrapper commonFieldProps={commonFieldProps} model={inputModel}>
          {fieldToRender}
        </ReferencedFieldWrapper>
      )}
    </OptionalParamWrapper>
  );
};
